/**
 * useBreakeven Hook
 *
 * Fetches breakeven analysis data for a given period.
 * Uses the breakeven-analysis Edge Function for calculations.
 *
 * Part of US-P7-003: Breakeven Analysis
 *
 * @example
 * const {
 *   data,
 *   chartData,
 *   isLoading,
 *   error,
 *   refetch
 * } = useBreakeven({
 *   periodStart: '2025-01-01',
 *   periodEnd: '2025-01-31'
 * })
 *
 * @example
 * const {
 *   scenario,
 *   comparison,
 *   calculate,
 *   reset,
 *   isCalculating
 * } = useWhatIfScenario({
 *   periodStart: '2025-01-01',
 *   periodEnd: '2025-01-31'
 * })
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'

// =============================================================================
// Constants
// =============================================================================

const FUNCTION_NAME = 'breakeven-analysis'

// =============================================================================
// Edge Function Helper
// =============================================================================

/**
 * Call the breakeven-analysis Edge Function
 *
 * @param {string} path - The endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 * @throws {Error} On network or API error
 */
async function callBreakevenFunction(path, options = {}) {
  const { params, method = 'GET', body } = options

  let fullPath = path ? `${FUNCTION_NAME}/${path}` : FUNCTION_NAME
  if (params) {
    fullPath += `?${params.toString()}`
  }

  const invokeOptions = { method }
  if (body) {
    invokeOptions.body = JSON.stringify(body)
    invokeOptions.headers = { 'Content-Type': 'application/json' }
  }

  const { data, error } = await supabase.functions.invoke(fullPath, invokeOptions)

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  // Check for API-level errors in response
  if (data?.error) {
    const err = new Error(data.message || data.error)
    err.retryable = data.retryable ?? false
    throw err
  }

  return data
}

// =============================================================================
// Main Hook: useBreakeven
// =============================================================================

/**
 * Hook to fetch breakeven analysis data
 *
 * @param {Object} options - Hook options
 * @param {string} options.periodStart - Start date in YYYY-MM-DD format
 * @param {string} options.periodEnd - End date in YYYY-MM-DD format
 * @param {boolean} [options.enabled=true] - Whether to fetch data
 * @returns {Object} Hook state and actions
 */
export function useBreakeven({ periodStart, periodEnd, enabled = true }) {
  const [data, setData] = useState(null)
  const [chartData, setChartData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Build query params
  const params = useMemo(() => {
    if (!periodStart || !periodEnd) return null
    const searchParams = new URLSearchParams()
    searchParams.set('period_start', periodStart)
    searchParams.set('period_end', periodEnd)
    return searchParams
  }, [periodStart, periodEnd])

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!params || !enabled) return

    setIsLoading(true)
    setError(null)

    try {
      // Fetch breakeven and chart data in parallel
      const [breakevenResponse, chartResponse] = await Promise.all([
        callBreakevenFunction('', { params }),
        callBreakevenFunction('chart-data', { params })
      ])

      setData(breakevenResponse)
      setChartData(chartResponse)
    } catch (err) {
      console.error('[useBreakeven] Error fetching data:', err)
      setError(err.message || 'Failed to fetch breakeven data')
    } finally {
      setIsLoading(false)
    }
  }, [params, enabled])

  // Fetch on mount and when params change
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Derived metrics for UI convenience
  const metrics = useMemo(() => {
    if (!data) return null

    return {
      // Breakeven points
      breakevenKg: data.breakeven_kg,
      breakevenRevenue: data.breakeven_revenue,

      // Current performance
      currentKg: data.current_kg,
      currentRevenue: data.current_revenue,
      performancePercent: data.performance_percent,
      safetyMarginPercent: data.safety_margin_percent,

      // Contribution metrics
      contributionPerKg: data.weighted_avg_contribution_per_kg,
      contributionPercent: data.weighted_avg_contribution_percent,
      totalContribution: data.total_contribution,

      // Cost breakdown
      fixedCosts: data.fixed_costs,
      variableCosts: data.variable_costs,
      variableCostPerKg: data.variable_cost_per_kg,

      // Status
      status: data.status,
      isHealthy: data.status === 'ok',
      hasData: data.current_kg > 0,
      aboveBreakeven: data.safety_margin_percent !== null && data.safety_margin_percent > 0
    }
  }, [data])

  return {
    // Raw data
    data,
    chartData,
    metrics,

    // Period info
    periodStart: data?.period_start || periodStart,
    periodEnd: data?.period_end || periodEnd,

    // Status
    isLoading,
    error,
    status: data?.status || null,

    // Actions
    refetch: fetchData
  }
}

// =============================================================================
// What-If Scenario Hook
// =============================================================================

/**
 * Hook for what-if scenario modelling
 *
 * @param {Object} options - Hook options
 * @param {string} options.periodStart - Start date in YYYY-MM-DD format
 * @param {string} options.periodEnd - End date in YYYY-MM-DD format
 * @returns {Object} Hook state and actions
 */
export function useWhatIfScenario({ periodStart, periodEnd }) {
  const [scenario, setScenario] = useState(null)
  const [strainsData, setStrainsData] = useState(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [isFetchingStrains, setIsFetchingStrains] = useState(false)
  const [error, setError] = useState(null)

  // Current adjustments state
  const [adjustments, setAdjustments] = useState({
    fixedCostsAdjustment: 0,
    priceAdjustmentPercent: 0,
    excludeStrains: []
  })

  // Build query params
  const params = useMemo(() => {
    if (!periodStart || !periodEnd) return null
    const searchParams = new URLSearchParams()
    searchParams.set('period_start', periodStart)
    searchParams.set('period_end', periodEnd)
    return searchParams
  }, [periodStart, periodEnd])

  // Fetch strains list for exclusion UI
  const fetchStrains = useCallback(async () => {
    if (!params) return

    setIsFetchingStrains(true)
    try {
      const response = await callBreakevenFunction('strains', { params })
      setStrainsData(response.strains || [])
    } catch (err) {
      console.error('[useWhatIfScenario] Error fetching strains:', err)
    } finally {
      setIsFetchingStrains(false)
    }
  }, [params])

  // Fetch strains on mount
  useEffect(() => {
    fetchStrains()
  }, [fetchStrains])

  /**
   * Calculate a what-if scenario with the current adjustments
   */
  const calculate = useCallback(async (customAdjustments = null) => {
    if (!params) {
      setError('Period dates are required')
      return
    }

    const adjustmentsToUse = customAdjustments || adjustments

    setIsCalculating(true)
    setError(null)

    try {
      const response = await callBreakevenFunction('what-if', {
        params,
        method: 'POST',
        body: {
          fixed_costs_adjustment: adjustmentsToUse.fixedCostsAdjustment,
          price_adjustment_percent: adjustmentsToUse.priceAdjustmentPercent,
          exclude_strains: adjustmentsToUse.excludeStrains
        }
      })

      setScenario(response)
    } catch (err) {
      console.error('[useWhatIfScenario] Error calculating scenario:', err)
      setError(err.message || 'Failed to calculate scenario')
    } finally {
      setIsCalculating(false)
    }
  }, [params, adjustments])

  /**
   * Reset scenario and adjustments
   */
  const reset = useCallback(() => {
    setScenario(null)
    setAdjustments({
      fixedCostsAdjustment: 0,
      priceAdjustmentPercent: 0,
      excludeStrains: []
    })
    setError(null)
  }, [])

  /**
   * Update fixed costs adjustment
   */
  const setFixedCostsAdjustment = useCallback((value) => {
    setAdjustments(prev => ({ ...prev, fixedCostsAdjustment: value }))
  }, [])

  /**
   * Update price adjustment percentage
   */
  const setPriceAdjustmentPercent = useCallback((value) => {
    setAdjustments(prev => ({ ...prev, priceAdjustmentPercent: value }))
  }, [])

  /**
   * Toggle strain exclusion
   */
  const toggleStrainExclusion = useCallback((strainId) => {
    setAdjustments(prev => {
      const isExcluded = prev.excludeStrains.includes(strainId)
      return {
        ...prev,
        excludeStrains: isExcluded
          ? prev.excludeStrains.filter(id => id !== strainId)
          : [...prev.excludeStrains, strainId]
      }
    })
  }, [])

  /**
   * Set all excluded strains at once
   */
  const setExcludedStrains = useCallback((strainIds) => {
    setAdjustments(prev => ({ ...prev, excludeStrains: strainIds }))
  }, [])

  // Comparison metrics derived from scenario
  const comparison = useMemo(() => {
    if (!scenario?.comparison) return null

    return {
      breakevenKgChange: scenario.comparison.breakeven_kg_change,
      breakevenKgChangePercent: scenario.comparison.breakeven_kg_change_percent,
      safetyMarginChange: scenario.comparison.safety_margin_change,
      isImprovement: scenario.comparison.breakeven_kg_change !== null
        ? scenario.comparison.breakeven_kg_change < 0
        : null
    }
  }, [scenario])

  return {
    // Scenario data
    scenario,
    comparison,

    // Available strains for exclusion
    strains: strainsData || [],
    isFetchingStrains,

    // Current adjustments
    adjustments,

    // Adjustment setters
    setFixedCostsAdjustment,
    setPriceAdjustmentPercent,
    toggleStrainExclusion,
    setExcludedStrains,

    // Status
    isCalculating,
    error,
    hasScenario: scenario !== null,

    // Actions
    calculate,
    reset
  }
}

// =============================================================================
// Combined Hook: useBreakevenWithScenarios
// =============================================================================

/**
 * Combined hook for breakeven analysis with built-in scenario support
 *
 * @param {Object} options - Hook options
 * @param {string} options.periodStart - Start date in YYYY-MM-DD format
 * @param {string} options.periodEnd - End date in YYYY-MM-DD format
 * @param {boolean} [options.enabled=true] - Whether to fetch data
 * @returns {Object} Combined hook state and actions
 */
export function useBreakevenWithScenarios({ periodStart, periodEnd, enabled = true }) {
  const breakeven = useBreakeven({ periodStart, periodEnd, enabled })
  const scenario = useWhatIfScenario({ periodStart, periodEnd })

  return {
    // Breakeven data
    ...breakeven,

    // Scenario support
    scenario: scenario.scenario,
    comparison: scenario.comparison,
    strains: scenario.strains,
    adjustments: scenario.adjustments,

    // Scenario setters
    setFixedCostsAdjustment: scenario.setFixedCostsAdjustment,
    setPriceAdjustmentPercent: scenario.setPriceAdjustmentPercent,
    toggleStrainExclusion: scenario.toggleStrainExclusion,
    setExcludedStrains: scenario.setExcludedStrains,

    // Scenario actions
    calculateScenario: scenario.calculate,
    resetScenario: scenario.reset,

    // Scenario status
    isCalculating: scenario.isCalculating,
    hasScenario: scenario.hasScenario,
    scenarioError: scenario.error
  }
}

// =============================================================================
// Default Export
// =============================================================================

export default useBreakeven
